import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  MenuItem,
  Typography,
} from '@material-ui/core'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { DeviceData } from '../form-values'
import { Dialog } from '../../../dialog/Dialog'
import { FormGroup } from '../../../form/FormGroup'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { validators } from '../../../../validation/validators'
import { theme } from '../../../../theme'
import {
  ScheduleFragment,
  VentilationDeviceType,
} from '../../../../generated/graphql'
import { airFlowUnit, temperatureUnit } from '../../../../util/formatters'

type Props = {
  open: boolean
  onSubmit: (values: DeviceData) => void
  defaultValues?: Partial<DeviceData>
  onClose: () => void
  isCreate: boolean
  onDelete?: () => void
  schedules: ScheduleFragment[]
}

export function VentilationDeviceDialog({
  open,
  onSubmit,
  onClose,
  defaultValues,
  isCreate,
  onDelete,
  schedules,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { control, errors, getValues, trigger } = useForm<DeviceData>({
    defaultValues,
  })

  const title = isCreate
    ? t('energySystem.parameters.ventilation.dialog.addDevice')
    : defaultValues?.deviceName ?? ''

  const onSave = async () => {
    const noError = await trigger()
    if (noError) onSubmit(getValues())
  }

  return (
    <Dialog title={title} open={open} onClose={onClose}>
      <form>
        <DialogContent>
          <FormGroup>
            <Grid container key="general" item alignItems="center" spacing={1}>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="deviceName"
                  error={errors.deviceName}
                  label={t('energySystem.parameters.ventilation.deviceName')}
                  rules={validators.required}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  control={control}
                  name="effectedArea"
                  error={errors.effectedArea}
                  label={t('energySystem.parameters.ventilation.effectedArea')}
                  rules={validators.required}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="airFlow"
                  error={errors.airFlow}
                  label={t('energySystem.parameters.ventilation.airFlow')}
                  rules={{
                    ...validators.required,
                    ...validators.positiveDecimalZero,
                  }}
                  InputProps={{
                    endAdornment: <Typography>{airFlowUnit}</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="heatRecoveryEfficiency"
                  error={errors.heatRecoveryEfficiency}
                  label={t(
                    'energySystem.parameters.ventilation.heatRecoveryEfficiency',
                  )}
                  rules={{
                    ...validators.required,
                    ...validators.efficiencyZero,
                  }}
                  style={{ overflowX: 'clip' }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="SFP"
                  error={errors.SFP}
                  label={t('energySystem.parameters.ventilation.SFP')}
                  rules={{
                    ...validators.required,
                    ...validators.positiveDecimalZero,
                  }}
                />
              </Grid>
              <Grid item xs={8}>
                <ControlledTextField
                  control={control}
                  name="deviceScheduleId"
                  error={errors.deviceScheduleId}
                  label={t('energySystem.parameters.ventilation.usageProfile')}
                  rules={validators.required}
                  select
                >
                  {Object.values(schedules).map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="systemType"
                  error={errors.systemType}
                  label={t('energySystem.parameters.ventilation.systemType')}
                  rules={validators.required}
                  select
                >
                  {Object.values(VentilationDeviceType).map((vdType) => (
                    <MenuItem key={vdType} value={vdType}>
                      {t(
                        `energySystem.parameters.ventilation.systemTypes.${vdType}`,
                      )}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
            </Grid>

            <Box my={2}>
              <Divider />
            </Box>

            <Grid
              container
              key="general_1"
              item
              alignItems="center"
              spacing={1}
            >
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="demandBasedUsageCoefficient"
                  error={errors.demandBasedUsageCoefficient}
                  label={t(
                    'energySystem.parameters.ventilation.demandBasedUsageCoefficient',
                  )}
                  rules={{ ...validators.required, ...validators.efficiency }}
                  style={{ overflowX: 'clip' }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="exhaustAirMinimumTemp"
                  error={errors.exhaustAirMinimumTemp}
                  label={t(
                    'energySystem.parameters.ventilation.exhaustAirMinimumTemp',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  defaultValue="0"
                  style={{ overflowX: 'clip' }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="coolingCoil"
                  error={errors.coolingCoil}
                  label={t('energySystem.parameters.ventilation.coolingCoil')}
                  rules={validators.required}
                  select
                >
                  <MenuItem key="v-type-0" value={0}>
                    {t(
                      `energySystem.parameters.ventilation.dialog.existsSelection.${0}`,
                    )}
                  </MenuItem>
                  <MenuItem key="v-type-1" value={1}>
                    {t(
                      `energySystem.parameters.ventilation.dialog.existsSelection.${1}`,
                    )}
                  </MenuItem>
                </ControlledTextField>
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="supplyAirTemp"
                  error={errors.supplyAirTemp}
                  label={t('energySystem.parameters.ventilation.supplyAirTemp')}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  defaultValue="0"
                  style={{ overflowX: 'clip' }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="indoorAirTemp"
                  error={errors.indoorAirTemp}
                  label={t('energySystem.parameters.ventilation.indoorAirTemp')}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  defaultValue="0"
                  style={{ overflowX: 'clip' }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="returnAirTemp"
                  error={errors.returnAirTemp}
                  label={t('energySystem.parameters.ventilation.returnAirTemp')}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  defaultValue="0"
                  style={{ overflowX: 'clip' }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="chilldrop"
                  error={errors.chilldrop}
                  label={t('energySystem.parameters.ventilation.chilldrop')}
                  rules={{ ...validators.required, ...validators.percent }}
                  InputProps={{
                    endAdornment: <Typography>%</Typography>,
                  }}
                  defaultValue="0"
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="chilldropTempLimit"
                  error={errors.chilldropTempLimit}
                  label={t(
                    'energySystem.parameters.ventilation.chilldropTempLimit',
                  )}
                  rules={{ ...validators.required, ...validators.decimal }}
                  InputProps={{
                    endAdornment: <Typography>{temperatureUnit}</Typography>,
                  }}
                  defaultValue="0"
                  style={{ overflowX: 'clip' }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  control={control}
                  name="exhaustAirHeatPump"
                  error={errors.exhaustAirHeatPump}
                  label={t(
                    'energySystem.parameters.ventilation.exhaustAirHeatPump',
                  )}
                  rules={validators.required}
                  select
                >
                  <MenuItem key="v-type-0" value={0}>
                    {t(
                      `energySystem.parameters.ventilation.dialog.existsSelection.${0}`,
                    )}
                  </MenuItem>
                  <MenuItem key="v-type-1" value={1}>
                    {t(
                      `energySystem.parameters.ventilation.dialog.existsSelection.${1}`,
                    )}
                  </MenuItem>
                </ControlledTextField>
              </Grid>
            </Grid>
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
            <Box>
              {onDelete && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onDelete}
                >
                  {t('common.remove')}
                </Button>
              )}
            </Box>
            <Box flexGrow={1} />
            <Button variant="contained" onClick={onClose}>
              {t('common.cancel')}
            </Button>

            <Button variant="contained" color="primary" onClick={onSave}>
              {isCreate
                ? t(
                    'energySystem.parameters.ventilation.dialog.addDeviceButton',
                  )
                : t(
                    'energySystem.parameters.ventilation.dialog.updateDeviceButton',
                  )}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}
